<template>
  <div
    class="package"
    :class="{
      current: showCurrent,
      recommended: showRecommended,
    }"
  >
    <div v-if="showCurrent" class="current-tag">
      Current Plan
    </div>

    <div v-if="showRecommended" class="recommended-tag">
      Recommended
    </div>

    <h3 class="name">
      {{ pack.name }}
    </h3>

    <div class="relative">
      <div class="price mb-0">
        <span class="price-unit">$</span>
        <span class="price-amount" :class="subscriptionClass">
          {{ plan.price }}
        </span>
        <span class="text-xxs">/month</span>
      </div>

      <p v-if="subscription === 'Annual'" class="subscription-info mt-1">
        billed annually with a
        <span class="text-success font-bold animate__animated animate__flash"
          >{{ plan.discountPercentage }}% discount</span
        >
      </p>

      <button
        v-if="subscription === 'Monthly'"
        class="text-success text-xxs font-semibold underline mt-1 outline-none"
        @click="$emit('set-annual-subscription')"
      >
        Select yearly billing and save!
      </button>
    </div>
    <!-- .relative -->

    <div v-if="pack.features" class="features mt-6">
      <div class="feature font-lato text-base flex justify-center items-center">
        <span class="max-emails">{{ maxEmails }}</span>

        Emails Included
        <v-icon
          v-tippy
          :content="campaignsTooltip"
          class="help-icon"
          name="regular/question-circle"
          scale=".61"
        />
      </div>

      <div class="feature">
        <img src="@/assets/icons/tick.svg" alt="Tick Icon" class="tick-icon" />
        Download Newsletters
      </div>

      <div class="feature">
        <img src="@/assets/icons/tick.svg" alt="Tick Icon" class="tick-icon" />
        Auto-Import Customers
      </div>

      <div class="feature" :class="{ unavailable: pack.id === 'free' }">
        <img src="@/assets/icons/tick.svg" alt="Tick Icon" class="tick-icon" />
        Manage Unsubscribes
      </div>

      <div class="feature" :class="{ unavailable: pack.id === 'free' }">
        <img src="@/assets/icons/tick.svg" alt="Tick Icon" class="tick-icon" />
        Mailing List Validation
      </div>

      <div class="feature" :class="{ unavailable: pack.id === 'free' }">
        <img src="@/assets/icons/tick.svg" alt="Tick Icon" class="tick-icon" />
        Analytics & Tracking
      </div>

      <div class="feature" :class="{ unavailable: pack.id === 'free' }">
        <img src="@/assets/icons/tick.svg" alt="Tick Icon" class="tick-icon" />
        Email SFP Security
      </div>

      <div
        v-if="$has(pack, 'features.customElements')"
        class="feature"
        :class="{ unavailable: !pack.features.customElements }"
      >
        <img src="@/assets/icons/tick.svg" alt="Tick Icon" class="tick-icon" />
        Upload Images
      </div>

      <div class="feature">
        <img src="@/assets/icons/tick.svg" alt="Tick Icon" class="tick-icon" />
        Expert Support
      </div>

      <div
        v-if="$has(pack, 'features.removePromoBranding')"
        class="feature"
        :class="{ unavailable: !pack.features.removePromoBranding }"
      >
        <img
          v-if="pack.features.removePromoBranding"
          src="@/assets/icons/tick.svg"
          alt="Tick Icon"
          class="tick-icon"
        />
        No Promo Branding
      </div>
    </div>

    <Button
      :href="plan.upgradeUrl"
      class="sm mx-auto py-1"
      :class="upgradeButtonClass(pack)"
      :disabled="showCurrent || loading"
      :loading="loading"
      @click="onPackageSelect($event, pack)"
    >
      {{ upgradeButtonLabel(pack) }}
    </Button>
  </div>
</template>

<script>
import { getAccountId } from "@/lib/auth";

export default {
  name: "Package",
  props: {
    index: {
      type: Number,
      required: true,
    },
    pack: {
      type: Object,
      required: true,
    },
    subscription: {
      type: String,
      required: true,
    },
    showRecommended: {
      type: Boolean,
      default: false,
    },
    subscriberCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    campaignsTooltip() {
      const maxEmails = this.$get(this.pack, "features.maxEmails");
      const maxCampaigns = Math.round(
        Math.floor(maxEmails / this.subscriberCount)
      ).toLocaleString();
      const campaigns = maxCampaigns === 1 ? "campaign" : "campaigns";

      return `You have ${this.subscriberCount?.toLocaleString()} subscribers which means you can send up to ${maxCampaigns} ${campaigns} per month <em>(daily sending limits apply)</em>.`;
    },
    maxEmails() {
      const maxEmails = this.$get(this.pack, "features.maxEmails") || "0";
      return Number(maxEmails).toLocaleString();
    },
    plan() {
      return this.pack?.plans
        ?.filter((plan) => plan.subscription === this.subscription)
        .reduce((acc) => acc);
    },
    showCurrent() {
      return this.plan?.current;
    },
    subscriptionClass() {
      return `${this.subscription.toLowerCase()}-pricing`;
    },
  },
  methods: {
    upgradeButtonClass(pack) {
      // If they're not on a premium plan then set to link styling...
      if (!pack.current && pack.id === "free") return "link text-brand";

      return "primary";
    },
    upgradeButtonLabel(pack) {
      if (this.loading) return "Loading...";

      if (this.showCurrent) return "Current Plan";

      if (pack.id === "free") return "Downgrade";

      return "Get Features";
    },
    onPackageSelect(event, pack) {
      if (pack.id === "free") {
        event.preventDefault();

        this.$emit("downgrade", this.plan?.upgradeUrl);

        return false;
      }

      this.loading = true;

      this.$gtm.trackEvent({
        event: "promo-select-package",
        packageName: this.pack.name,
        packagePrice: this.plan?.price,
        accountId: getAccountId(),
      });
    },
  },
};
</script>

<style scoped>
.package {
  @apply flex flex-col text-center border border-grey-lighter shadow rounded-lg py-6 relative bg-brand-light;

  &.recommended {
    @apply border border-grey-light shadow-md bg-info-lighter;
    transform: scale(1.04);
  }
}

.name {
  @apply text-support-light font-light font-sans mb-4;
}

.price {
  @apply flex items-baseline justify-center text-grey-dark text-sm font-sans font-normal leading-none;
}

.price-unit {
  @apply text-sm text-support-lighter;
  vertical-align: super;
}

.price-amount {
  @apply font-lato text-support-light text-4xl;

  &.annual-pricing {
    @apply text-success;
  }
}

.subscription-info {
  @apply text-support-lighter mb-0 italic text-xxs;
}

.current-tag {
  @apply absolute bg-white rounded block border;
  color: #a2a2d2;
  border-color: #cfcfe4;
  font-size: 10px;
  right: -5px;
  padding: 2px 5px;
  top: -5px;
  transform: rotate(1deg);
}

.recommended-tag {
  @apply .current-tag text-info-dark font-semibold border-grey-light;

  left: -5px;
  right: auto;
  transform: rotate(-1.5deg);
}

.features {
  @apply mb-6;
}

.feature {
  @apply mb-2 text-support-light text-sm;
  text-shadow: 1px 1px 2px white;

  &.unavailable {
    @apply opacity-50 italic line-through;
    text-shadow: none;
  }
}

.help-icon {
  margin-left: 3px;
  top: -7px;
}

.cancel-button {
  @apply text-grey-dark border-grey-dark;
}

.max-emails {
  @apply text-brand mr-1;

  @screen md {
    @apply text-lg;
  }

  @screen lg {
    @apply text-xl;
    transform: rotate(-0.8deg);
  }
}

.tick-icon {
  @apply m-0;
  width: 12px;
}
</style>
